import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  SelectEntry,
  SelectTemplateDirective,
  createSelectOverlay,
  flatOptions,
  optionLabel,
  optionValue,
  selectEntries,
  selectListId,
} from './select-options';
import { sameValue } from './select.component';

/**
 * A multi select with these inputs: options as values or objects (optionLabel,
 * optionValue), a header with the "all" check box (showToggleAll), a search
 * field (filter, filterBy) and a close button, the chosen values as a list or
 * as chips (display) or through a template `appSelectTemplate="selectedItems"`.
 * The list stays open while the user checks entries. The arrow keys move
 * through it, Enter and Space check the entry, Escape and Tab close it.
 */
@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  template: `<div
      #origin
      class="app-multi-select {{ styleClass }}"
      [class.app-multi-select-open]="open"
      [class.app-multi-select-chip]="display === 'chip'"
      [class.app-multi-select-filled]="filled"
      [class.app-multi-select-clearable]="showClear"
      [class.app-focus]="focused"
      [class.app-disabled]="disabled"
      role="combobox"
      aria-haspopup="listbox"
      [attr.tabindex]="disabled ? null : 0"
      [attr.aria-expanded]="open"
      [attr.aria-controls]="open ? listId : null"
      [attr.aria-activedescendant]="activeId"
      [attr.aria-label]="ariaLabel"
      [attr.aria-labelledby]="ariaLabelledBy"
      [attr.aria-disabled]="disabled || null"
      (click)="toggle()"
      (keydown)="onKeydown($event)"
      (focus)="focused = true"
      (blur)="onBlur()"
    >
      <div class="app-multi-select-label-container">
        <div
          class="app-multi-select-label"
          [class.app-placeholder]="!filled"
          [class.app-multi-select-label-empty]="!filled && !placeholder"
        >
          @if (selectedItemsTemplate) {
            <ng-container
              *ngTemplateOutlet="
                selectedItemsTemplate;
                context: { $implicit: value }
              "
            ></ng-container>
          } @else if (display === 'chip') {
            @for (item of value || []; track $index) {
              <div class="app-multi-select-token">
                <span class="app-multi-select-token-label">{{
                  labelOf(item)
                }}</span>
                @if (!disabled) {
                  <span
                    class="app-multi-select-token-icon app-icon fa-solid fa-circle-xmark"
                    aria-hidden="true"
                    (click)="remove(item, $event)"
                  ></span>
                }
              </div>
            }
            @if (!filled) {
              {{ placeholder || 'empty' }}
            }
          } @else {
            {{ valuesAsString || placeholder || 'empty' }}
          }
        </div>
        @if (showClear && filled && !disabled) {
          <i
            class="app-multi-select-clear-icon app-icon fa-solid fa-xmark"
            aria-hidden="true"
            (click)="clear($event)"
          ></i>
        }
      </div>
      <div class="app-multi-select-trigger" aria-hidden="true">
        <span
          class="app-multi-select-trigger-icon app-icon fa-solid fa-chevron-down"
        ></span>
      </div>
    </div>
    <ng-template #panel>
      <!-- Handles the keys of the filter input inside, which has the focus. -->
      <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
      <div
        class="app-multi-select-panel {{ panelStyleClass }}"
        (keydown)="onKeydown($event)"
      >
        @if (showHeader) {
          <div class="app-multi-select-header">
            @if (showToggleAll) {
              <div
                class="app-checkbox"
                role="checkbox"
                tabindex="0"
                aria-label="All"
                [attr.aria-checked]="allChecked"
                (click)="toggleAll()"
                (keydown.space)="$event.preventDefault(); toggleAll()"
              >
                <div
                  class="app-checkbox-box"
                  [class.app-highlight]="allChecked"
                >
                  <span
                    class="app-checkbox-icon"
                    [class.app-checked]="allChecked"
                  ></span>
                </div>
              </div>
            }
            @if (filter) {
              <div class="app-multi-select-filter-container">
                <input
                  type="text"
                  autocomplete="off"
                  class="app-input app-multi-select-filter"
                  [value]="filterValue"
                  [attr.placeholder]="filterPlaceholder"
                  [attr.aria-controls]="listId"
                  [attr.aria-activedescendant]="activeId"
                  (input)="onFilterInput($event)"
                />
                <span
                  class="app-multi-select-filter-icon app-icon fa-solid fa-magnifying-glass"
                ></span>
              </div>
            }
            <button
              class="app-multi-select-close"
              type="button"
              aria-label="Close"
              (click)="hide()"
            >
              <span
                class="app-multi-select-close-icon app-icon fa-solid fa-xmark"
              ></span>
            </button>
          </div>
        }
        <div
          class="app-multi-select-items-wrapper"
          [style.max-height]="scrollHeight"
        >
          <ul
            class="app-multi-select-items"
            role="listbox"
            aria-multiselectable="true"
            [id]="listId"
          >
            @for (entry of entries; track $index) {
              <!-- The select box keeps the focus and moves through the options (aria-activedescendant). -->
              <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
              <li
                class="app-multi-select-item"
                role="option"
                [id]="listId + '-' + entry.index"
                [class.app-highlight]="isSelected(entry.option)"
                [class.app-multi-select-item-active]="
                  entry.index === activeIndex
                "
                [attr.aria-selected]="isSelected(entry.option)"
                (click)="toggleOption(entry.option)"
              >
                <div class="app-checkbox">
                  <div
                    class="app-checkbox-box"
                    [class.app-highlight]="isSelected(entry.option)"
                  >
                    <span
                      class="app-checkbox-icon"
                      [class.pi]="isSelected(entry.option)"
                      [class.app-checked]="isSelected(entry.option)"
                    ></span>
                  </div>
                </div>
                <span>{{ entry.label }}</span>
              </li>
            }
            @if (!entries.length) {
              <li class="app-multi-select-empty-message">
                {{ filterValue ? emptyFilterMessage : emptyMessage }}
              </li>
            }
          </ul>
        </div>
      </div>
    </ng-template>`,
})
export class MultiSelectComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private cdr = inject(ChangeDetectorRef);

  @Input() options: unknown[] | null | undefined = [];
  @Input() optionLabel?: string;
  @Input() optionValue?: string;
  @Input() placeholder?: string;
  /** 'comma' lists the labels, 'chip' shows a chip per value. */
  @Input() display: 'comma' | 'chip' = 'comma';
  /** Up to this many labels in the 'comma' display, then "n items selected". */
  @Input() maxSelectedLabels = 3;
  @Input() showHeader = true;
  @Input() showToggleAll = true;
  @Input() filter = true;
  /** The fields the search looks at, separated by commas. */
  @Input() filterBy?: string;
  @Input() filterPlaceholder?: string;
  @Input() resetFilterOnHide = false;
  @Input() showClear = false;
  @Input() emptyMessage = 'No results found';
  @Input() emptyFilterMessage = 'No results found';
  @Input() scrollHeight = '200px';
  @Input() styleClass = '';
  @Input() panelStyleClass = '';
  @Input() ariaLabel?: string;
  /** Id of a visible label of the select box. */
  @Input() ariaLabelledBy?: string;
  @Input() disabled = false;

  /** The values a user chose. */
  @Output() valueChange = new EventEmitter<unknown[]>();

  @ViewChild('origin', { static: true })
  private origin!: ElementRef<HTMLElement>;
  @ViewChild('panel', { static: true })
  private panelTemplate!: TemplateRef<unknown>;
  @ContentChildren(SelectTemplateDirective)
  private templates?: QueryList<SelectTemplateDirective>;

  readonly listId = selectListId();
  value: unknown[] | null = null;
  entries: SelectEntry[] = [];
  filterValue = '';
  activeIndex = -1;
  focused = false;

  private overlayRef: OverlayRef | null = null;
  private onChange: (value: unknown[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get open() {
    return !!this.overlayRef?.hasAttached();
  }

  get filled() {
    return !!this.value?.length;
  }

  get activeId() {
    return this.open && this.activeIndex >= 0
      ? `${this.listId}-${this.activeIndex}`
      : null;
  }

  get selectedItemsTemplate() {
    return (
      this.templates?.find((t) => t.name === 'selectedItems')?.template ?? null
    );
  }

  /** Whether all options the list shows are checked. */
  get allChecked() {
    return (
      !!this.entries.length &&
      this.entries.every((entry) => this.isSelected(entry.option))
    );
  }

  get valuesAsString() {
    const values = this.value || [];
    if (!values.length) {
      return '';
    }
    return values.length <= this.maxSelectedLabels
      ? values.map((value) => this.labelOf(value)).join(', ')
      : `${values.length} items selected`;
  }

  private get fields() {
    return { optionLabel: this.optionLabel, optionValue: this.optionValue };
  }

  ngOnChanges(): void {
    this.updateEntries();
  }

  ngOnDestroy(): void {
    const ref = this.overlayRef;
    this.overlayRef = null;
    ref?.dispose();
  }

  writeValue(value: unknown[] | null): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  isSelected(option: unknown) {
    const value = optionValue(option, this.fields);
    return !!this.value?.some((item) => sameValue(item, value));
  }

  labelOf(value: unknown) {
    const option = flatOptions(this.options, false, this.fields).find(
      (candidate) => sameValue(optionValue(candidate, this.fields), value)
    );
    return option === undefined
      ? String(value ?? '')
      : optionLabel(option, this.fields);
  }

  toggle() {
    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    if (this.open || this.disabled) {
      return;
    }
    const origin = this.origin.nativeElement;
    const ref = createSelectOverlay(this.overlay, origin);
    this.overlayRef = ref;
    this.activeIndex = -1;
    ref.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    ref.outsidePointerEvents().subscribe((event) => {
      if (!origin.contains(event.target as Node)) {
        this.hide();
      }
    });
    ref.detachments().subscribe(() => this.detached(ref));
    ref.overlayElement
      .querySelector<HTMLInputElement>('.app-multi-select-filter')
      ?.focus();
    this.cdr.markForCheck();
  }

  hide() {
    if (this.overlayRef) {
      this.detached(this.overlayRef);
    }
  }

  /** Empties the search field and shows all options again. */
  resetFilter() {
    this.filterValue = '';
    this.updateEntries();
    this.cdr.markForCheck();
  }

  toggleOption(option: unknown) {
    const value = optionValue(option, this.fields);
    const values = this.value || [];
    this.setValue(
      this.isSelected(option)
        ? values.filter((item) => !sameValue(item, value))
        : [...values, value]
    );
  }

  /** Checks all options the list shows, or unchecks them when all are. */
  toggleAll() {
    const shown = this.entries.map((entry) =>
      optionValue(entry.option, this.fields)
    );
    const values = this.value || [];
    const rest = values.filter(
      (item) => !shown.some((value) => sameValue(item, value))
    );
    this.setValue(this.allChecked ? rest : [...rest, ...shown]);
  }

  remove(item: unknown, event: Event) {
    event.stopPropagation();
    this.setValue(
      (this.value || []).filter((value) => !sameValue(value, item))
    );
  }

  // An empty list, not null, keeps the callers' `.length` safe.
  clear(event: Event) {
    event.stopPropagation();
    this.setValue([]);
  }

  onFilterInput(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value;
    this.updateEntries();
    this.activeIndex = -1;
    this.overlayRef?.updatePosition();
  }

  onKeydown(event: KeyboardEvent) {
    if (this.disabled || event.defaultPrevented) {
      return;
    }
    const target = event.target as HTMLElement;
    const inFilter = target.classList.contains('app-multi-select-filter');
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        if (!this.open) {
          this.show();
        } else {
          this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Enter':
      case ' ':
        if (event.key === ' ' && inFilter) {
          break;
        }
        event.preventDefault();
        event.stopPropagation();
        if (!this.open) {
          this.show();
        } else if (this.activeIndex >= 0) {
          this.toggleOption(this.entries[this.activeIndex].option);
        } else if (event.key === 'Enter') {
          this.hide();
        }
        break;
      case 'Escape':
        if (this.open) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
        }
        break;
      case 'Tab':
        if (target === this.origin.nativeElement || inFilter) {
          this.hide();
        }
        break;
    }
  }

  onBlur() {
    this.focused = false;
    if (!this.open) {
      this.onTouched();
    }
  }

  private setValue(value: unknown[]) {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private detached(ref: OverlayRef) {
    if (this.overlayRef !== ref) {
      return;
    }
    const focusInPanel = !!ref.overlayElement?.contains(document.activeElement);
    this.overlayRef = null;
    ref.dispose();
    if (this.resetFilterOnHide) {
      this.filterValue = '';
      this.updateEntries();
    }
    if (focusInPanel) {
      this.origin.nativeElement.focus({ preventScroll: true });
    }
    this.onTouched();
    this.cdr.markForCheck();
  }

  private updateEntries() {
    this.entries = selectEntries(
      this.options,
      false,
      this.filterValue,
      this.filterBy,
      this.fields
    );
  }

  private moveActive(step: number) {
    if (!this.entries.length) {
      return;
    }
    const next = this.activeIndex + step;
    this.activeIndex = Math.max(0, Math.min(this.entries.length - 1, next));
    this.cdr.detectChanges();
    const id = this.activeId;
    if (id) {
      this.overlayRef?.overlayElement
        .querySelector(`#${id}`)
        ?.scrollIntoView?.({ block: 'nearest' });
    }
  }
}
